import { zodResolver } from "@hookform/resolvers/zod";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import {
  Alert,
  Avatar,
  Button,
  DropdownSelect,
  ErrorMessage,
  Icon,
  Loading,
  Modal,
  Spinner,
  TextArea,
  TextInput,
  TimeFrameInput,
  Tooltip,
  Typography,
} from "@/components/atoms";
import { PhoneNumberInput } from "@/components/atoms/PhoneNumber/PhoneNumberInput";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useRequestPasswordResetMutation } from "@/redux/apis/auth/authApi";
import { useGetClientProfileQuery, useUpdateClientProfileMutation } from "@/redux/apis/clientProfile/clientProfileApi";
import { clientProfileApi } from "@/redux/apis/clientProfile/clientProfileApi";
import { ClientProfileCompany, ClientProfilePreferences } from "@/redux/apis/clientProfile/types";
import { useDeleteAvatarMutation, useUpdateAvatarMutation, useUpdateProfileMutation } from "@/redux/apis/profile/profileApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";
import { getAssetUrl, addToast, clsx } from "@/utils";
import { useClientScope } from "./ClientScopeContext";

const TITLE_OPTIONS = ["Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "Sir."];

const getInitials = (firstName: string, lastName: string) => `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

type EditSection = "avatar" | "personal" | "account" | "company" | "preference" | null;

export const SettingsProfile = () => {
  // Operator-managed client when set; undefined in self-service.
  const { accountId, isViewOnly } = useClientScope();
  const isManaged = !!accountId;
  const { data: profile, isLoading } = useGetClientProfileQuery(accountId ? { accountId } : undefined);
  const [editSection, setEditSection] = useState<EditSection>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  if (isLoading || !profile) return <Loading />;

  const { personal, company, preferences, avatar, metadata } = profile;
  const fullName = `${personal.title ? personal.title + " " : ""}${personal.first_name} ${personal.last_name}`.trim();
  const avatarUrl = avatar ? getAssetUrl(avatar, { height: 160, width: 160, fit: "cover" }) : undefined;

  return (
    <div>
      <Typography variant="title" className="mb-6 block">
        {isManaged ? "Client Profile" : "My Profile"}
      </Typography>

      <div className="flex flex-col gap-4">
        {/* Profile Header */}
        <div className={clsx("rounded-lg border p-4", editSection === "avatar" ? "border-primary" : "border-neutral-mid-gray")}>
          {editSection === "avatar" ? (
            <AvatarEditSection avatarUrl={avatarUrl} onCancel={() => setEditSection(null)} onSaved={() => setEditSection(null)} />
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-4">
                <Avatar size="md" src={avatarUrl} alt={fullName} className="h-12 w-12 bg-neutral-mid-gray">
                  {!avatar && (
                    <Typography variant="small" className="text-neutral-dark-gray">
                      {getInitials(personal.first_name, personal.last_name)}
                    </Typography>
                  )}
                </Avatar>
                <div>
                  <Typography variant="action">{fullName}</Typography>
                  <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
                    Customer
                  </Typography>
                </div>
              </div>
              {!isManaged && (
                <Button
                  variant="secondary"
                  size="sm"
                  endIcon="Edit2"
                  className="w-full sm:w-auto"
                  onClick={() => setEditSection("avatar")}
                  disabled={editSection !== null}
                >
                  Edit Profile Picture
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Personal Information */}
        <div className={clsx("rounded-lg border p-5", editSection === "personal" ? "border-primary" : "border-neutral-mid-gray")}>
          {editSection === "personal" ? (
            <PersonalInfoForm
              personal={personal}
              accountId={accountId}
              onCancel={() => setEditSection(null)}
              onSaved={() => setEditSection(null)}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Typography variant="title" className="text-neutral-silver-gray">
                  Personal Information
                </Typography>
                <Button
                  variant="secondary"
                  size="sm"
                  endIcon="Edit2"
                  onClick={() => setEditSection("personal")}
                  disabled={editSection !== null}
                >
                  Edit
                </Button>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 md:grid-cols-4">
                <FieldDisplay label="First Name" value={personal.first_name} />
                <FieldDisplay label="Last Name" value={personal.last_name} />
                <FieldDisplay label="Title" value={personal.title} />
                <FieldDisplay label="Mobile no." value={personal.mobile} />
              </div>
            </>
          )}
        </div>

        {/* Account Details */}
        <div className={clsx("rounded-lg border p-5", editSection === "account" ? "border-primary" : "border-neutral-mid-gray")}>
          {editSection === "account" ? (
            <AccountDetailsForm
              email={personal.email}
              pendingEmail={personal.pending_email}
              accountId={accountId}
              isManaged={isManaged}
              onCancel={() => setEditSection(null)}
              onSaved={() => setEditSection(null)}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Typography variant="title" className="text-neutral-silver-gray">
                  Account Details
                </Typography>
                <Button
                  variant="secondary"
                  size="sm"
                  endIcon="Edit2"
                  onClick={() => setEditSection("account")}
                  disabled={editSection !== null}
                >
                  Edit
                </Button>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 md:grid-cols-4">
                <div>
                  <FieldDisplay label="Email (Username)" value={personal.email} />
                  {personal.pending_email && (
                    <Typography variant="small" className="mt-1 break-all">
                      Pending: <span className="text-warning">{personal.pending_email}</span>
                      <Tooltip content="You will need to log in using this email to confirm the change." placement="right">
                        <Icon name="Danger" variant="Bold" size="sm" className="ml-1 inline-block align-middle text-warning" />
                      </Tooltip>
                    </Typography>
                  )}
                </div>
                <div>
                  <Typography variant="paragraph" className="text-neutral-dark-gray">
                    Password
                  </Typography>
                  {isViewOnly ? (
                    // View-only (removed client): show disabled, not clickable (DEV-1177).
                    <Typography variant="action" className="mt-1 text-neutral-silver-gray">
                      Change Password
                    </Typography>
                  ) : (
                    <Typography variant="action" className="mt-1 cursor-pointer text-info" onClick={() => setShowPasswordModal(true)}>
                      Change Password
                    </Typography>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Company / Cost Centre / Executive Assistant — single card with dividers */}
        <div className={clsx("rounded-lg border p-5", editSection === "company" ? "border-primary" : "border-neutral-mid-gray")}>
          {editSection === "company" ? (
            <CompanyForm
              company={company}
              isCompanyEditable={metadata.is_company_editable}
              accountId={accountId}
              onCancel={() => setEditSection(null)}
              onSaved={() => setEditSection(null)}
            />
          ) : (
            <>
              {/* Company */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Typography variant="title" className="text-neutral-silver-gray">
                  Company
                </Typography>
                <Button
                  variant="secondary"
                  size="sm"
                  endIcon="Edit2"
                  onClick={() => setEditSection("company")}
                  disabled={editSection !== null}
                >
                  Edit
                </Button>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 md:grid-cols-4">
                <FieldDisplay label="Company Name" value={company.company_name} />
                <FieldDisplay label="Job Title" value={company.job_title} />
                <FieldDisplay label="Office Address" value={company.office_address} className="md:col-span-2" />
              </div>

              <hr className="my-5 border-neutral-mid-gray" />

              {/* Cost Centre Details */}
              <Typography variant="title" className="text-neutral-silver-gray">
                Cost Centre Details
              </Typography>
              <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 md:grid-cols-4">
                <FieldDisplay label="Cost Centre Name" value={company.cost_centre_name} />
                <FieldDisplay label="Cost Centre ID Number" value={company.cost_centre_id_number} />
                <FieldDisplay label="Company Division" value={company.company_division} />
              </div>

              <hr className="my-5 border-neutral-mid-gray" />

              {/* Executive Assistant */}
              <Typography variant="title" className="text-neutral-silver-gray">
                Executive Assistant
              </Typography>
              <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 md:grid-cols-4">
                <FieldDisplay label="Assistant's Name" value={company.ea_name} />
                <FieldDisplay label="Mobile no." value={company.ea_contact_number} />
                <FieldDisplay label="Email Address" value={company.ea_email} />
              </div>
            </>
          )}
        </div>

        {/* Preference */}
        <div className={clsx("rounded-lg border p-5", editSection === "preference" ? "border-primary" : "border-neutral-mid-gray")}>
          {editSection === "preference" ? (
            <PreferenceForm
              preferences={preferences}
              accountId={accountId}
              onCancel={() => setEditSection(null)}
              onSaved={() => setEditSection(null)}
            />
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Typography variant="title" className="text-neutral-silver-gray">
                  Preference
                </Typography>
                <Button
                  variant="secondary"
                  size="sm"
                  endIcon="Edit2"
                  onClick={() => setEditSection("preference")}
                  disabled={editSection !== null}
                >
                  Edit
                </Button>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 md:grid-cols-4">
                <FieldDisplay label="Seating Preference" value={preferences.seating} />
                <FieldDisplay label="Radio Preference" value={preferences.radio} />
                <FieldDisplay label="Quiet Ride" value={preferences.quiet_ride} />
                <FieldDisplay label="Vehicle Temp" value={preferences.vehicle_temp ? `${preferences.vehicle_temp}° C` : undefined} />
                <FieldDisplay
                  label="Airport Waiting Time"
                  value={
                    preferences.airport_waiting_time
                      ? `${preferences.airport_waiting_time} ${preferences.airport_waiting_timeframe === "minute" ? "min" : "hour"}${
                          Number(preferences.airport_waiting_time) !== 1 ? "s" : ""
                        }`
                      : undefined
                  }
                />
                <FieldDisplay label="Passenger Notes" value={preferences.driver_notes} className="md:col-span-3" />
              </div>
            </>
          )}
        </div>
      </div>

      <PasswordChangeModal
        isOpen={showPasswordModal}
        accountId={accountId}
        isManaged={isManaged}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
};

// ─── Avatar Edit Section ─────────────────────────────────────────────────────

// Self-service only: avatar mutations use baseQueryWithAccount (no accountId override),
// so they always target the logged-in account. The Edit trigger is hidden when managed.
const AvatarEditSection = ({ avatarUrl, onCancel, onSaved }: { avatarUrl?: string; onCancel: () => void; onSaved: () => void }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pendingRemove, setPendingRemove] = useState(false);
  const dispatch = useAppDispatch();
  const [updateAvatar, { isLoading: isUploading }] = useUpdateAvatarMutation();
  const [deleteAvatar, { isLoading: isDeleting }] = useDeleteAvatarMutation();
  const isLoading = isUploading || isDeleting;
  const hasChanges = !!selectedFile || pendingRemove;

  const MAX_SIZE = 5 * 1024 * 1024; // 5MB
  const MIN_DIMENSION = 200;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_SIZE) {
      addToast("danger", "File is too large. Max 5MB.");
      return;
    }

    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
        addToast("danger", `Image must be at least ${MIN_DIMENSION}×${MIN_DIMENSION}px.`);
        return;
      }
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    };
    img.onerror = () => {
      addToast("danger", "Invalid image file.");
    };
    img.src = URL.createObjectURL(file);
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    try {
      if (pendingRemove) {
        await deleteAvatar().unwrap();
        addToast("success", "Photo removed");
      } else if (selectedFile) {
        const formData = new FormData();
        formData.append("avatar", selectedFile);
        await updateAvatar(formData).unwrap();
        addToast("success", "Avatar updated successfully");
      }
      dispatch(clientProfileApi.util.invalidateTags(["ClientProfile"]));
      onSaved();
    } catch {
      addToast("danger", pendingRemove ? "Failed to remove photo" : "Failed to update avatar");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Typography variant="title" className="text-neutral-silver-gray">
          Profile Picture
        </Typography>
        <div className="flex items-center gap-2">
          <Button variant="tertiary" size="sm" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={isLoading || !hasChanges}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
      <div className="mt-5 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
        <input ref={fileInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleFileSelect} />
        <div className="shrink-0">
          {preview && !pendingRemove ? (
            <div className="flex h-[120px] w-[120px] items-center justify-center rounded-full border-2 border-dashed border-neutral-mid-gray p-1">
              <img src={preview} alt="Preview" className="h-full w-full rounded-full object-cover" />
            </div>
          ) : avatarUrl && !pendingRemove ? (
            <div className="flex h-[120px] w-[120px] items-center justify-center rounded-full border-2 border-dashed border-neutral-mid-gray p-1">
              <img src={avatarUrl} alt="Current" className="h-full w-full rounded-full object-cover" />
            </div>
          ) : (
            <div className="flex h-[120px] w-[120px] items-center justify-center rounded-full border-2 border-dashed border-neutral-mid-gray bg-neutral-surface-gray">
              <Icon name="User" size="xxl" className="text-neutral-silver-gray" />
            </div>
          )}
        </div>
        <div className="w-full sm:min-w-0 sm:flex-1">
          <Typography variant="action">Update your profile picture</Typography>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            This is the photo that appears on your RideMinder account and is shown to your driver when you book a trip.
          </Typography>
          <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center">
            <Button variant="primary" size="sm" className="w-full lg:w-auto" onClick={() => fileInputRef.current?.click()}>
              Upload New Photo
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="w-full lg:w-auto"
              disabled={isLoading || pendingRemove || (!avatarUrl && !preview)}
              onClick={() => {
                setPendingRemove(true);
                setPreview(null);
                setSelectedFile(null);
              }}
            >
              Remove Photo
            </Button>
          </div>
          <Typography variant="small" className="mt-3 text-neutral-silver-gray">
            JPG or PNG, at least 200×200px. Max 5MB.
          </Typography>
        </div>
      </div>
    </div>
  );
};

const FieldDisplay = ({ label, value, className }: { label: string; value?: string | null; className?: string }) => (
  <div className={clsx("min-w-0", className)}>
    <Typography variant="paragraph" className="text-neutral-dark-gray">
      {label}
    </Typography>
    <Typography variant="action" className="mt-1 break-words text-neutral-black">
      {value || "-"}
    </Typography>
  </div>
);

// ─── Personal Information Form ───────────────────────────────────────────────

const personalSchema = z.object({
  first_name: z.string().min(2, "First name must be at least 2 characters").max(45),
  last_name: z.string().min(2, "Last name must be at least 2 characters").max(45),
  title: z.string(),
  mobile: z.string().min(1, "Mobile number is required"),
});

type PersonalFormData = z.infer<typeof personalSchema>;

const PersonalInfoForm = ({
  personal,
  accountId,
  onCancel,
  onSaved,
}: {
  personal: { first_name: string; last_name: string; title: string; mobile: string };
  accountId?: string;
  onCancel: () => void;
  onSaved: () => void;
}) => {
  const [updateProfile, { isLoading }] = useUpdateClientProfileMutation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PersonalFormData>({
    resolver: zodResolver(personalSchema),
    defaultValues: {
      first_name: personal.first_name,
      last_name: personal.last_name,
      title: personal.title,
      mobile: personal.mobile,
    },
  });

  const onSubmit = async (data: PersonalFormData) => {
    try {
      await updateProfile({
        accountId,
        personal: {
          first_name: data.first_name,
          last_name: data.last_name,
          title: data.title,
          mobile: data.mobile,
        },
      }).unwrap();
      addToast("success", "Personal information updated");
      onSaved();
    } catch {
      addToast("danger", "Failed to update personal information");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Typography variant="title" className="text-neutral-silver-gray">
          Personal Information
        </Typography>
        <div className="hidden items-center gap-2 sm:flex">
          <Button variant="tertiary" size="sm" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            First Name
          </Typography>
          <TextInput {...register("first_name")} size="md" maxLength={45} hasError={!!errors.first_name} />
          <ErrorMessage errors={errors} name="first_name" />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Last Name
          </Typography>
          <TextInput {...register("last_name")} size="md" maxLength={45} hasError={!!errors.last_name} />
          <ErrorMessage errors={errors} name="last_name" />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Title
          </Typography>
          <Controller
            control={control}
            name="title"
            render={({ field }) => <DropdownSelect options={TITLE_OPTIONS} value={field.value} onChange={field.onChange} size="md" />}
          />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Mobile no.
          </Typography>
          <PhoneNumberInput control={control} name="mobile" size="md" hasError={!!errors.mobile} />
          <ErrorMessage errors={errors} name="mobile" />
        </div>
      </div>
      <div className="mt-5 flex flex-col gap-2 sm:hidden">
        <Button variant="primary" size="md" type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Saving..." : "Save"}
        </Button>
        <Button variant="secondary" size="md" type="button" className="w-full" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// ─── Account Details Form ────────────────────────────────────────────────────

const accountSchema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email address"),
});

type AccountFormData = z.infer<typeof accountSchema>;

const AccountDetailsForm = ({
  email,
  pendingEmail,
  accountId,
  isManaged,
  onCancel,
  onSaved,
}: {
  email: string;
  pendingEmail: string | null;
  accountId?: string;
  isManaged: boolean;
  onCancel: () => void;
  onSaved: () => void;
}) => {
  const { logout } = useKindeAuth();
  // Email changes go through the account-management profile endpoint (not the client PATCH), which
  // creates the Kinde identity + sets pending_email and enforces uniqueness. Self-service targets the
  // logged-in account; an operator editing a managed client passes the client's accountId so Kinde
  // creates the identity for the client (who then confirms by signing in with the new email).
  const [updateEmail, { isLoading }] = useUpdateProfileMutation();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, dirtyFields },
  } = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: { email },
  });

  const onSubmit = async (_data: AccountFormData) => {
    if (dirtyFields.email) {
      setShowEmailModal(true);
      return;
    }
    // No changes
    onSaved();
  };

  const handleEmailModalContinue = async () => {
    const formEmail = getValues("email");
    setIsUpdatingEmail(true);
    try {
      // Both self and managed go through the account-management/Kinde endpoint; the managed case
      // targets the client's account via accountId. Kinde creates the identity + sets pending_email.
      await updateEmail(isManaged ? { accountId, email: formEmail } : { email: formEmail }).unwrap();
      setShowEmailModal(false);
      if (isManaged) {
        // Operator editing a client: don't sign the operator out — the client confirms the change by
        // signing in with their new email.
        setIsUpdatingEmail(false);
        addToast("success", "Client login email update requested");
        onSaved();
      } else {
        // Self-service: sign out so the user can confirm by signing in with their new email.
        logout();
      }
    } catch (e) {
      // Surface server errors (e.g. "email already in use" from Kinde) instead of logging out.
      // Close the modal so the user is back on the form to correct the email.
      setIsUpdatingEmail(false);
      setShowEmailModal(false);
      const messages = getErrorMessages(e as Error);
      (messages.length ? messages : ["Failed to update email"]).forEach((m) => addToast("danger", m));
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Typography variant="title" className="text-neutral-silver-gray">
            Account Details
          </Typography>
          <div className="hidden items-center gap-2 sm:flex">
            <Button variant="tertiary" size="sm" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <div>
            <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
              Email (Username)
            </Typography>
            <TextInput {...register("email")} size="md" hasError={!!errors.email} />
            <ErrorMessage errors={errors} name="email" />
            {pendingEmail && (
              <Typography variant="paragraph" className="mt-1 break-all">
                Pending: <span className="text-warning">{pendingEmail}</span>
                <Tooltip content="You will need to log in using this email to confirm the change." placement="right">
                  <Icon name="Danger" variant="Bold" size="sm" className="ml-1 inline-block align-middle text-warning" />
                </Tooltip>
              </Typography>
            )}
          </div>
        </div>
        <div className="mt-5 flex flex-col gap-2 sm:hidden">
          <Button variant="primary" size="md" type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
          <Button variant="secondary" size="md" type="button" className="w-full" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        </div>
      </form>

      {/* Email Change Confirmation Modal */}
      <Modal open={showEmailModal} onClose={() => !isUpdatingEmail && setShowEmailModal(false)} variant="web-small">
        {isUpdatingEmail && <Loading />}
        <div className="w-full px-6 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center">
              <div className="mb-4 flex items-center justify-center rounded-lg bg-warning-light p-3">
                <Icon name="Warning2" variant="Bold" size={36} className="text-warning" />
              </div>
            </div>
            <Typography variant="h1">{isManaged ? "Update Client Login Email" : "Update Login Email"}</Typography>
            <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
              {isManaged
                ? "The client will need to sign in with the new email to confirm this change."
                : "You'll be signed out automatically to apply this change. Sign back in using your new email to continue."}
            </Typography>
          </div>
          <div className="mt-8 flex flex-col-reverse gap-3 md:flex-row">
            <Button
              onClick={() => setShowEmailModal(false)}
              variant="secondary"
              className="w-full md:flex-1"
              size="lg"
              disabled={isUpdatingEmail}
            >
              Cancel
            </Button>
            <Button onClick={handleEmailModalContinue} className="w-full md:flex-1" size="lg" disabled={isUpdatingEmail}>
              Continue
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── Password Change Modal ──────────────────────────────────────────────────

const PasswordChangeModal = ({
  isOpen,
  accountId,
  isManaged,
  onClose,
}: {
  isOpen: boolean;
  accountId?: string;
  isManaged: boolean;
  onClose: () => void;
}) => {
  const { logout } = useKindeAuth();
  const selfAccountId = useAppSelector(accountIdSelector);
  const targetAccountId = accountId ?? selfAccountId;
  const [requestPasswordReset, { isLoading }] = useRequestPasswordResetMutation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleChangePasswordClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    try {
      setErrors([]);
      await requestPasswordReset(targetAccountId).unwrap();
      setShowConfirmation(false);
      onClose();
      // Operator acting on a client: send the client a reset link, stay signed in.
      if (isManaged) {
        addToast("success", "Password reset email sent to the client");
      } else {
        logout();
      }
    } catch (e) {
      setErrors(getErrorMessages(e as Error));
      setShowConfirmation(false);
    }
  };

  const handleClose = () => {
    setShowConfirmation(false);
    setErrors([]);
    onClose();
  };

  return (
    <>
      {/* Main Modal */}
      <Modal open={isOpen && !showConfirmation} onClose={handleClose} variant="web-small">
        <div className="w-full px-6 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center">
              <div className="mb-4 flex items-center justify-center rounded-lg bg-primary-light p-3">
                <Icon name="Lock1" variant="Bold" size={36} className="text-primary" />
              </div>
            </div>
            <Typography variant="h1">Change Password</Typography>
            <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
              {isManaged
                ? "We'll email the client a link to create a new password. You'll stay signed in."
                : "To change your password, you'll be signed out and redirected to create a new password."}
            </Typography>
          </div>

          {errors.length > 0 && (
            <div className="my-4">
              {errors.map((e, i) => (
                <Alert key={i} type="danger" message={e} className="my-4" />
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-col-reverse gap-3 md:flex-row">
            <Button onClick={handleClose} variant="secondary" className="w-full md:flex-1" size="lg" disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleChangePasswordClick} className="w-full md:flex-1" size="lg" disabled={isLoading}>
              Change Password
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal open={showConfirmation} onClose={handleClose} variant="web-small">
        <div className="w-full px-6 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center">
              <div className="mb-4 flex items-center justify-center rounded-lg bg-warning-light p-3">
                <Icon name="Warning2" variant="Bold" size={36} className="text-warning" />
              </div>
            </div>
            <Typography variant="h1">Confirm Password Change</Typography>
            <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
              {isManaged
                ? "We'll email the client a link to create a new password. Are you sure you want to continue?"
                : "You'll be signed out and redirected to the sign-in page to create a new password. Are you sure you want to continue?"}
            </Typography>
          </div>

          <div className="mt-8 flex flex-col-reverse gap-3 md:flex-row">
            <Button onClick={handleClose} variant="secondary" className="w-full md:flex-1" size="lg" disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isLoading} className="w-full md:flex-1" size="lg">
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Spinner variant="light" />
                  <span className="ml-2">Processing...</span>
                </div>
              ) : (
                "Continue"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

// ─── Company Form ────────────────────────────────────────────────────────────

const companySchema = z.object({
  company_name: z.string().max(100).optional().or(z.literal("")),
  job_title: z.string().max(100).optional().or(z.literal("")),
  office_address: z.string().max(512).optional().or(z.literal("")),
  cost_centre_name: z.string().max(45).optional().or(z.literal("")),
  cost_centre_id_number: z.string().max(45).optional().or(z.literal("")),
  company_division: z.string().max(45).optional().or(z.literal("")),
  ea_name: z.string().max(65).optional().or(z.literal("")),
  ea_email: z.string().email("Please enter a valid email address").max(100).optional().or(z.literal("")),
  ea_contact_number: z.string().optional().or(z.literal("")),
});

type CompanyFormData = z.infer<typeof companySchema>;

const CompanyForm = ({
  company,
  isCompanyEditable,
  accountId,
  onCancel,
  onSaved,
}: {
  company: ClientProfileCompany;
  isCompanyEditable: boolean;
  accountId?: string;
  onCancel: () => void;
  onSaved: () => void;
}) => {
  const [updateProfile, { isLoading }] = useUpdateClientProfileMutation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      company_name: company.company_name || "",
      job_title: company.job_title || "",
      office_address: company.office_address || "",
      cost_centre_name: company.cost_centre_name || "",
      cost_centre_id_number: company.cost_centre_id_number || "",
      company_division: company.company_division || "",
      ea_name: company.ea_name || "",
      ea_email: company.ea_email || "",
      ea_contact_number: company.ea_contact_number || "",
    },
  });

  const onSubmit = async (data: CompanyFormData) => {
    try {
      const { company_name: _company_name, ...rest } = data;
      const payload = isCompanyEditable ? data : rest;
      await updateProfile({ accountId, company: payload }).unwrap();
      addToast("success", "Company details updated");
      onSaved();
    } catch {
      addToast("danger", "Failed to update company details");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Typography variant="title" className="text-neutral-silver-gray">
          Company
        </Typography>
        <div className="hidden items-center gap-2 sm:flex">
          <Button variant="tertiary" size="sm" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Company Name
          </Typography>
          <TextInput {...register("company_name")} size="md" maxLength={100} disabled={!isCompanyEditable} />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Job Title
          </Typography>
          <TextInput {...register("job_title")} size="md" maxLength={100} />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Office Address
          </Typography>
          <TextInput {...register("office_address")} size="md" maxLength={512} />
        </div>
      </div>

      <hr className="my-5 border-neutral-mid-gray" />

      <Typography variant="title" className="text-neutral-silver-gray">
        Cost Centre Details
      </Typography>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Cost Centre Name
          </Typography>
          <TextInput {...register("cost_centre_name")} size="md" maxLength={45} />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Cost Centre ID Number
          </Typography>
          <TextInput {...register("cost_centre_id_number")} size="md" maxLength={45} />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Company Division
          </Typography>
          <TextInput {...register("company_division")} size="md" maxLength={45} />
        </div>
      </div>

      <hr className="my-5 border-neutral-mid-gray" />

      <Typography variant="title" className="text-neutral-silver-gray">
        Executive Assistant
      </Typography>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Assistant&apos;s Name
          </Typography>
          <TextInput {...register("ea_name")} size="md" maxLength={65} />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Mobile no.
          </Typography>
          <PhoneNumberInput control={control} name="ea_contact_number" size="md" />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Email Address
          </Typography>
          <TextInput {...register("ea_email")} size="md" maxLength={100} hasError={!!errors.ea_email} />
          <ErrorMessage errors={errors} name="ea_email" />
        </div>
      </div>
      <div className="mt-5 flex flex-col gap-2 sm:hidden">
        <Button variant="primary" size="md" type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Saving..." : "Save"}
        </Button>
        <Button variant="secondary" size="md" type="button" className="w-full" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// ─── Preference Form ─────────────────────────────────────────────────────────

const QUIET_RIDE_OPTIONS = ["Yes, quiet ride", "No, chatty ride"];
const RADIO_OPTIONS = ["Yes, radio on", "No, radio off"];
const VEHICLE_TEMP_OPTIONS = ["18", "19", "20", "21", "22", "23", "24", "25"];
const SEATING_OPTIONS = ["Front seat", "Back seat"];

const preferenceSchema = z.object({
  seating: z.string(),
  radio: z.string(),
  quiet_ride: z.string(),
  vehicle_temp: z.string(),
  airport_waiting_time: z.string(),
  airport_waiting_timeframe: z.string(),
  driver_notes: z.string().max(1024, "Passenger notes must be 1024 characters or less"),
});

type PreferenceFormData = z.infer<typeof preferenceSchema>;

const PreferenceForm = ({
  preferences,
  accountId,
  onCancel,
  onSaved,
}: {
  preferences: ClientProfilePreferences;
  accountId?: string;
  onCancel: () => void;
  onSaved: () => void;
}) => {
  const [updateProfile, { isLoading }] = useUpdateClientProfileMutation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PreferenceFormData>({
    resolver: zodResolver(preferenceSchema),
    defaultValues: {
      seating: preferences.seating || "",
      radio: preferences.radio || "",
      quiet_ride: preferences.quiet_ride || "",
      vehicle_temp: preferences.vehicle_temp || "",
      airport_waiting_time: preferences.airport_waiting_time || "",
      airport_waiting_timeframe: preferences.airport_waiting_timeframe || "hour",
      driver_notes: preferences.driver_notes || "",
    },
  });

  const onSubmit = async (data: PreferenceFormData) => {
    try {
      await updateProfile({
        accountId,
        preferences: {
          seating: data.seating,
          radio: data.radio,
          quiet_ride: data.quiet_ride,
          vehicle_temp: data.vehicle_temp,
          airport_waiting_time: data.airport_waiting_time,
          airport_waiting_timeframe: data.airport_waiting_timeframe,
          driver_notes: data.driver_notes,
        },
      }).unwrap();
      addToast("success", "Preferences updated");
      onSaved();
    } catch {
      addToast("danger", "Failed to update preferences");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Typography variant="title" className="text-neutral-silver-gray">
          Preference
        </Typography>
        <div className="hidden items-center gap-2 sm:flex">
          <Button variant="tertiary" size="sm" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Seating Preference
          </Typography>
          <Controller
            control={control}
            name="seating"
            render={({ field }) => (
              <DropdownSelect options={SEATING_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Select" size="md" />
            )}
          />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Radio Preference
          </Typography>
          <Controller
            control={control}
            name="radio"
            render={({ field }) => (
              <DropdownSelect options={RADIO_OPTIONS} value={field.value} onChange={field.onChange} placeholder="No preference" size="md" />
            )}
          />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Quiet Ride
          </Typography>
          <Controller
            control={control}
            name="quiet_ride"
            render={({ field }) => (
              <DropdownSelect options={QUIET_RIDE_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Select" size="md" />
            )}
          />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Vehicle Temp
          </Typography>
          <Controller
            control={control}
            name="vehicle_temp"
            render={({ field }) => (
              <DropdownSelect options={VEHICLE_TEMP_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Select" size="md" />
            )}
          />
        </div>
        <div>
          <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
            Airport Waiting Time
          </Typography>
          <TimeFrameInput
            className="w-48"
            min="0"
            max="999"
            inputSize="md"
            timeName="airport_waiting_time"
            timeFrameName="airport_waiting_timeframe"
            register={register}
            options={["hour", "minute"]}
          />
        </div>
      </div>
      <div className="mt-4">
        <Typography variant="paragraph" className="mb-1 text-neutral-dark-gray">
          Passenger Notes
        </Typography>
        <TextArea {...register("driver_notes")} rows={3} maxLength={1024} placeholder="Notes for your driver" />
        <ErrorMessage errors={errors} name="driver_notes" />
      </div>
      <div className="mt-5 flex flex-col gap-2 sm:hidden">
        <Button variant="primary" size="md" type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Saving..." : "Save"}
        </Button>
        <Button variant="secondary" size="md" type="button" className="w-full" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
